import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseSafeFormView, BaseView, BaseText } from '@/components/base'
import { AlertDescription, FixedBottomButton, AppButton } from '@/components'
import { StickyHeaderNavigation } from '@/components'
import { HeaderProfileAndCrypto } from '@/components/HeaderProfileAndCrypto'
import { HeaderConverted } from '@/components/HeaderConverted'
import { TransactionInfos } from '@/components/TransactionInfos'
import { TransactionDetailsSkeleton } from '@/components/LoadingSkeletons'
import { useTheme } from '@/hooks/useTheme'
import { useGetTransaction, Transaction } from '@/domain/transactions'
import { getTransactionHeaderTitle } from '@/utils/getTransactionHeaderTitle'
import { links } from '@/constants/links'
import { openLink } from '@/utils/openLink'
import { useSplitTransactionStore } from '@/store/useSplitTransactionStore'

export const TransactionDetailsScreen: React.FC = () => {
  const { transactionId } = useParams<{ transactionId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const setTransaction = useSplitTransactionStore((s) => s.setTransaction)

  const {
    data: transactionDetails,
    isLoading,
    refetch,
    error,
  } = useGetTransaction({
    transactionId: transactionId!,
    enabled: !!transactionId,
  })

  const handleBack = () => {
    navigate(-1)
  }

  const handleRefresh = () => {
    refetch()
  }

  const headerTitle = transactionDetails
    ? getTransactionHeaderTitle(transactionDetails, t)
    : t('translation:details') || 'Details'

  // Determine if we should show additional description about total amount
  const canRenderTotalDescription =
    transactionDetails?.type === 'card_write_off'

  // Determine if this is a conversion transaction
  const isConvert =
    transactionDetails?.type === 'swap_convert' ||
    transactionDetails?.type === 'swap_sell'

  // Determine if this is a kem-to-kem transaction
  const isKemToKem = transactionDetails?.type === 'kem_to_kem'

  // Handle support link press
  const handleSupportPress = () => {
    openLink(links.support)
  }

  // Handle split request
  const handleSplitRequest = () => {
    if (transactionDetails) {
      setTransaction(transactionDetails)
      navigate('/split-request/select-users')
    }
  }

  // Check if split button should be shown
  // Enabled for card purchases, withdrawals, and internal sends in pending or completed states
  const splitEligibleTypes = [
    'card_write_off',
    'boxo_purchase',
    'external_withdraw',
    'kem_to_kem',
  ]
  const splitEligibleStates = ['completed', 'processing']
  const canSplitTransaction =
    transactionDetails &&
    splitEligibleTypes.includes(transactionDetails.type) &&
    splitEligibleStates.includes(transactionDetails.state)

  if (error) {
    return (
      <BaseSafeFormView flex={1} bgColor="background.screen">
        {/* Fixed Header Navigation */}
        <StickyHeaderNavigation
          title={t('translation:error') || 'Error'}
          onBackPress={handleBack}
        />

        <BaseView flex={1} center px={16}>
          <BaseText fontSize={48} style={{ marginBottom: 16 }}>
            ❌
          </BaseText>
          <BaseText
            fontSize={18}
            fontFamily="semibold"
            color="label.default"
            textAlign="center"
            style={{ marginBottom: 8 }}
          >
            {t('translation:failedToLoadTransaction') ||
              'Failed to load transaction'}
          </BaseText>
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.description"
            textAlign="center"
            style={{ marginBottom: 24 }}
          >
            {t('translation:pleaseCheckConnectionAndTryAgain') ||
              'Please check your connection and try again'}
          </BaseText>
          <BaseView
            py={12}
            px={24}
            style={{
              backgroundColor: theme.colors.background.buttonPrimaryEnabled,
              borderRadius: '12px',
              cursor: 'pointer',
            }}
            onClick={handleRefresh}
          >
            <BaseText
              fontSize={16}
              fontFamily="medium"
              color="label.buttonPrimaryEnabled"
            >
              {t('translation:tryAgain') || 'Try Again'}
            </BaseText>
          </BaseView>
        </BaseView>
      </BaseSafeFormView>
    )
  }

  return (
    <BaseView>
      <BaseView flex={1} bgColor="background.screen">
        {/* Fixed Header Navigation */}
        <StickyHeaderNavigation title={headerTitle} onBackPress={handleBack} />

        {/* Scrollable Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            paddingBottom: '100px',
          }}
        >
          {/* Commenting out old skeleton approach - keeping for reference */}
          {/* {isLoading && !transactionDetails ? (
            <TransactionDetailsSkeleton
              variant="standard"
              showSplitButton={false}
              showExplorerButton={true}
            />
          ) : transactionDetails ? ( */}

          <BaseView gap={isKemToKem ? 28 : 40} pt={32} px={12}>
            {/* Transaction Header */}
            {isConvert ? (
              <HeaderConverted
                transaction={transactionDetails}
                isLoading={isLoading}
              />
            ) : (
              <HeaderProfileAndCrypto
                transaction={transactionDetails}
                isLoading={isLoading}
              />
            )}

            <BaseView gap={24}>
              {/* Transaction Details */}
              <TransactionInfos
                transaction={transactionDetails}
                isLoading={isLoading}
              />

              {/* View on Blockchain Explorer Button */}
              {transactionDetails?.tx_scan_link && (
                <BaseView
                  py={12}
                  px={16}
                  row
                  align="center"
                  justify="center"
                  gap={8}
                  style={{
                    backgroundColor:
                      theme.colors.background.buttonSecondaryEnabled,
                    borderRadius: 12,
                    cursor: 'pointer',
                  }}
                  onClick={() => openLink(transactionDetails.tx_scan_link!)}
                >
                  <BaseText
                    fontSize={14}
                    fontFamily="medium"
                    color="label.buttonSecondaryEnabled"
                  >
                    {t('translation:viewInExplorer') || 'View in Explorer'}
                  </BaseText>
                </BaseView>
              )}

              {/* Split Request Button */}
              {canSplitTransaction && (
                <BaseView mb={16}>
                  <AppButton
                    label={t('translation:splitRequest', {
                      defaultValue: 'Split request',
                    })}
                    iconName="groupIcon"
                    onPress={handleSplitRequest}
                    type="primaryGradient"
                  />
                </BaseView>
              )}

              {/* Alert Description - Support Info */}
              {!isLoading && (
                <AlertDescription
                  additionalTopDescription={
                    canRenderTotalDescription
                      ? t('translation:aboutTotalAmount')
                      : undefined
                  }
                  description={t('translation:contactUsDescription')}
                  linkText={t('translation:contactSupport')}
                  onPressLink={handleSupportPress}
                />
              )}
            </BaseView>
          </BaseView>

          {/* ) : (
            <BaseView flex={1} center py={40}>
              <BaseText
                fontSize={16}
                fontFamily="regular"
                color="label.description"
                textAlign="center"
              >
                {t('translation:transactionNotFound') ||
                  'Transaction not found'}
              </BaseText>
            </BaseView>
          )} */}
        </div>

        {/* Fixed Bottom Button */}
        <FixedBottomButton
          buttons={{
            label: t('translation:close') || 'Close',
            onPress: handleBack,
            type: 'secondaryGray',
            isLoading: isLoading,
          }}
        />
      </BaseView>
    </BaseView>
  )
}
